import { useEffect, useMemo, useState } from 'react';
import SkyMap from '../components/SkyMap';
import {
  fetchP9Samples,
  fetchP9SkyMap,
  type SamplesResponse,
  type SkyMapResponse,
} from '../lib/planet9';

interface Stats {
  totalSamples: number;
  meanA: number;
  meanE: number;
  meanI: number;
  meanQ: number;
  excludedRatio: number;
  byPrior: Record<string, number>;
}

function computeStats(s: SamplesResponse): Stats {
  const n = s.samples.length;
  let sa = 0, se = 0, si = 0, sq = 0, exc = 0;
  const byPrior: Record<string, number> = {};
  for (const x of s.samples) {
    sa += x.a_au;
    se += x.e;
    si += x.i_deg;
    sq += x.a_au * (1 - x.e);
    if (x.excluded_by.length > 0) exc += 1;
    byPrior[x.prior_id] = (byPrior[x.prior_id] || 0) + 1;
  }
  return {
    totalSamples: n,
    meanA: sa / n,
    meanE: se / n,
    meanI: si / n,
    meanQ: sq / n,
    excludedRatio: exc / n,
    byPrior,
  };
}

export default function PlanetNine() {
  const [skyMap, setSkyMap] = useState<SkyMapResponse | null>(null);
  const [samples, setSamples] = useState<SamplesResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchP9Samples(2000, 42), fetchP9SkyMap(8000, 42, 4)])
      .then(([s, sm]) => {
        if (cancelled) return;
        setSamples(s);
        setSkyMap(sm);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const stats = useMemo(() => (samples ? computeStats(samples) : null), [samples]);

  return (
    <div className="page">
      <div className="banner">
        <strong>📘 Visualisation pédagogique d'une hypothèse scientifique</strong>
        <span>
          Planet Nine n'a <strong>jamais</strong> été observée directement. Cette page transforme
          les priors publiés (Brown &amp; Batygin 2021) en une carte de probabilité visuelle
          pour aider à comprendre <em>où elle pourrait théoriquement se trouver</em> — pas où elle est.
        </span>
      </div>

      <h1>Planet Nine — explorateur d'hypothèse</h1>
      <p>
        Cette carte est obtenue en générant des milliers d'orbites candidates depuis les
        intervalles publiés (a, e, i), puis en marginalisant sur les angles inconnus
        (Ω, ω, M tirés uniformément). La densité observée illustre les zones du ciel où
        les modèles dynamiques privilégient sa présence — sans la confirmer.
      </p>
      <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>
        💡 Important : si Planet Nine existe, elle pourrait aussi être à un endroit
        que les modèles actuels ne prédisent pas. Les modèles sont des indices,
        pas des oracles.
      </p>

      <h2>Carte de densité posterior (RA / Dec)</h2>
      {loading && <p>Chargement des samples Planet9…</p>}
      {err && (
        <div className="banner">
          <strong>Erreur backend.</strong>
          <span>{err}</span>
        </div>
      )}
      {!loading && skyMap && <SkyMap data={skyMap} width={720} height={360} />}
      {skyMap && (
        <p style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 8 }}>
          {skyMap.n_samples.toLocaleString('fr-FR')} samples · tile {skyMap.tile_size_deg}° ·
          {skyMap.cached ? ' cache hit' : ' fresh'} · {skyMap.tiles.length} tuiles
        </p>
      )}

      {stats && (
        <>
          <h2>Statistiques de l'ensemble</h2>
          <div
            className="card"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 16,
            }}
          >
            <Stat label="Samples" value={stats.totalSamples.toLocaleString('fr-FR')} />
            <Stat label="⟨a⟩" value={`${stats.meanA.toFixed(0)} UA`} />
            <Stat label="⟨e⟩" value={stats.meanE.toFixed(3)} />
            <Stat label="⟨i⟩" value={`${stats.meanI.toFixed(1)}°`} />
            <Stat label="⟨q⟩ périhélie moyen" value={`${stats.meanQ.toFixed(0)} UA`} />
            <Stat label="% pénalisé" value={`${(stats.excludedRatio * 100).toFixed(1)} %`} />
          </div>
          <p style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 8 }}>
            Priors utilisés : {Object.entries(stats.byPrior)
              .map(([k, v]) => `${k} (${v})`)
              .join(' · ')}
          </p>
        </>
      )}

      <h2>Pour</h2>
      <ul>
        <li>Clusters d'arguments du périhélie observés chez les ETNO.</li>
        <li>Inclinaisons élevées de certains objets très distants.</li>
        <li>Outliers difficiles à expliquer sans perturbateur massif (2017 OF201, 2023 KQ14 / Ammonite).</li>
      </ul>

      <h2>Contre</h2>
      <ul>
        <li>Biais d'observation possibles dans les surveys nord vs sud.</li>
        <li>Modèles alternatifs : disque massif, biais de sélection des Sednoid.</li>
        <li>Aucune détection directe malgré plusieurs surveys ciblés (PS1, DES, ZTF, Subaru).</li>
      </ul>

      {skyMap && skyMap.excluded_zones.length > 0 && (
        <>
          <h2>Zones / contraintes d'exclusion</h2>
          <div className="card">
            <ul style={{ marginTop: 0 }}>
              {skyMap.excluded_zones.map((z) => (
                <li key={z.id}>
                  <strong>{z.id}</strong> — {z.summary}
                </li>
              ))}
            </ul>
            <p style={{ color: 'var(--text-dim)', fontSize: 12, marginBottom: 0 }}>
              Les coordonnées RA/Dec précises des zones exclues seront ajoutées
              quand elles seront extraites des suppléments papier (cf. v0.5).
            </p>
          </div>
        </>
      )}

      <h2>Statut</h2>
      <p style={{ color: 'var(--warn)' }}>
        Représentation probabiliste uniquement. Aucune cellule, aucune orbite affichée
        ne doit être interprétée comme une "position" de Planet Nine.
      </p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  );
}
